export interface UploadToPresignedUrlOptions {
  /**
   * The `Content-Type` header to send. Must match what the presigned URL was
   * signed for, or S3 rejects the upload with a signature mismatch.
   */
  readonly contentType?: string;
  /**
   * Called with an integer 0–100 as the upload progresses.
   */
  readonly onProgress?: (percentage: number) => void;
}

export interface PresignedUpload {
  /**
   * Resolves when the upload completes (HTTP 200); rejects on failure or abort.
   */
  readonly uploadPromise: Promise<void>;
  /**
   * Aborts the in-flight upload.
   */
  readonly abort: () => void;
}

/**
 * `PUT` a file/blob to a presigned S3 URL, reporting transfer progress via the
 * XHR upload events.
 *
 * @param body - the file or blob to upload
 * @param presignedUrl - the S3 URL to `PUT` to
 * @param options - optional content type and progress callback
 *
 * @returns a promise that settles with the transfer, plus an `abort` function
 */
export const uploadToPresignedUrl = (
  body: Blob,
  presignedUrl: string,
  { contentType, onProgress }: UploadToPresignedUrlOptions = {},
): PresignedUpload => {
  const xhr = new XMLHttpRequest();

  const uploadPromise = new Promise<void>((resolve, reject) => {
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.floor((event.loaded / event.total) * 100));
      }
    });

    xhr.addEventListener('loadend', () => {
      if (xhr.readyState === xhr.DONE && xhr.status === 200) {
        resolve();
      } else {
        reject(new Error(`File upload failed... XHR status: ${xhr.status}`));
      }
    });

    xhr.open('PUT', presignedUrl, true);
    if (contentType) xhr.setRequestHeader('Content-Type', contentType);
    xhr.send(body);
  });

  return {
    uploadPromise,
    abort: () => {
      if (xhr.readyState !== xhr.DONE) xhr.abort();
    },
  };
};
